import React, { useState, useMemo, useCallback, useEffect } from "react";
import SingleDatePicker from "./SingleDatePicker";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { rooms } from "../constants/rooms";
import { getDashboardWorkOrders } from "../api/workOrder";

// Reusable Button Component
const Button = ({ children, onClick, className, ...props }) => (
  <button
    className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:text-base ${className}`}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

// Reusable Select Component
const Select = ({ value, onChange, options, label, className }) => (
  <div className="flex flex-col w-full sm:w-auto">
    {label && (
      <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
    )}
    <select
      value={value}
      onChange={onChange}
      className={`border border-gray-300 rounded-lg px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 ${className}`}
      aria-label={label}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const WorkOrderCalendar = ({ globalSearchQuery = "" }) => {
  const [view, setView] = useState("week");
  const [sortBy, setSortBy] = useState("hourly");
  const [roomFilter, setRoomFilter] = useState("Room 1");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hideEmptyDays, setHideEmptyDays] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWorkOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const { weekStart, weekEnd } = getWeekRange(selectedDate);
    const startDate = formatDateYMD(weekStart);
    const endDate = formatDateYMD(weekEnd);
    const room = roomFilter;
    try {
      const response = await getDashboardWorkOrders(room, startDate, endDate);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch work orders");
      }
      setWorkOrders(response.data || []);
    } catch (err) {
      setError(err.message || "Network error");
      toast.error("Failed to load work orders");
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, roomFilter]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  const transformedWorkOrders = useMemo(() => {
    return workOrders.map((wo) => {
      let hour = null;
      let scheduledTime = wo.slot || "Not scheduled";

      if (wo.slot) {
        const match = wo.slot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (match) {
          let h = parseInt(match[1], 10);
          const period = match[3].toUpperCase();
          if (period === "PM" && h !== 12) h += 12;
          if (period === "AM" && h === 12) h = 0;
          hour = h;
        }
      }
      if (hour === null && wo.startTime) {
        const start = new Date(wo.startTime);
        if (!isNaN(start.getTime())) {
          hour = start.getHours();
          scheduledTime = start.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }

      return {
        ...wo,
        hour,
        scheduledTime,
        slot: wo.slot || "",
        duration: wo.duration || 30,
        date: wo.assignedDate
          ? new Date(`${wo.assignedDate.split("T")[0]}T00:00:00.000Z`)
          : null,
      };
    });
  }, [workOrders]);

  const getWeekRange = (date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diff);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return { weekStart, weekEnd };
  };

  // Helper to format date as yyyy-MM-dd
  const formatDateYMD = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const filteredWorkOrders = useMemo(() => {
    const { weekStart, weekEnd } = getWeekRange(selectedDate);

    const weekStartUTC = new Date(
      Date.UTC(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate(),
      ),
    );
    const weekEndUTC = new Date(
      Date.UTC(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate()),
    );

    return transformedWorkOrders.filter((wo) => {
      if (!wo.date || wo.room !== roomFilter) return false;

      const woDateUTC = wo.date;

      if (view === "day") {
        const selectedDateUTC = new Date(
          Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
          ),
        );
        return formatDateYMD(woDateUTC) === formatDateYMD(selectedDateUTC);
      }

      return woDateUTC >= weekStartUTC && woDateUTC <= weekEndUTC;
    });
  }, [transformedWorkOrders, view, selectedDate, roomFilter]);

  const roomOptions = useMemo(() => {
    return rooms.map((room) => ({
      value: room,
      label: room,
    }));
  }, []);

  // Week navigation handlers
  const goToPreviousWeek = () => {
    const { weekStart } = getWeekRange(selectedDate);
    const newDate = new Date(weekStart);
    newDate.setDate(weekStart.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const { weekStart } = getWeekRange(selectedDate);
    const newDate = new Date(weekStart);
    newDate.setDate(weekStart.getDate() + 7);
    setSelectedDate(newDate);
  };

  const goToCurrentWeek = () => {
    setSelectedDate(new Date());
  };

  const hours = Array.from({ length: 10 }, (_, i) => 8 + i);

  const dayEvents = useMemo(() => {
    if (view !== "day" || sortBy !== "hourly") return {};

    const events = {};
    hours.forEach((h) => (events[h] = []));

    filteredWorkOrders.forEach((wo) => {
      if (wo.hour === null || wo.hour < 0 || wo.hour > 23) return;

      if (!wo.date || formatDateYMD(wo.date) !== formatDateYMD(selectedDate))
        return;

      if (wo.hour < 8 || wo.hour > 17) return;

      events[wo.hour].push(wo);
    });

    Object.keys(events).forEach((h) => {
      events[h].sort((a, b) => (a.slot || "").localeCompare(b.slot || ""));
    });

    return events;
  }, [filteredWorkOrders, view, sortBy, selectedDate]);

  const groupedEvents = useMemo(() => {
    if (sortBy === "hourly" && view === "day") return {};

    const events = {};
    const { weekStart } = getWeekRange(selectedDate);

    if (sortBy === "room") {
      const roomKey = roomFilter;
      events[roomKey] = filteredWorkOrders.sort((a, b) =>
        (a.slot || "").localeCompare(b.slot || ""),
      );
      return events;
    }

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + i);
      const dayKey = formatDateYMD(dayDate);
      events[dayKey] = {};
      hours.forEach((h) => (events[dayKey][h] = []));
    }

    filteredWorkOrders.forEach((wo) => {
      if (wo.hour === null || wo.hour < 8 || wo.hour > 17) return;

      const dayKey = wo.date ? formatDateYMD(wo.date) : null;
      if (dayKey && events[dayKey]) {
        events[dayKey][wo.hour].push(wo);
      }
    });

    Object.keys(events).forEach((day) => {
      Object.keys(events[day]).forEach((h) => {
        events[day][h].sort((a, b) =>
          (a.slot || "").localeCompare(b.slot || ""),
        );
      });
    });

    return events;
  }, [filteredWorkOrders, view, sortBy, selectedDate, roomFilter]);

  // Visible days for the weekly grid
  const visibleDays = useMemo(() => {
    const { weekStart } = getWeekRange(selectedDate);
    const daysList = [];
    for (let day = 0; day < 7; day++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + day);
      const dayKey = formatDateYMD(dayDate);
      const hasWorkOrders = Object.keys(
        groupedEvents[dayKey] || {},
      ).some((hour) => (groupedEvents[dayKey][hour] || []).length > 0);
      const isWeekend = day === 5 || day === 6;
      if (hideEmptyDays && isWeekend && !hasWorkOrders) continue;
      daysList.push({ day, dayDate, dayKey });
    }
    if (daysList.length === 0) {
      for (let day = 0; day < 7; day++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + day);
        const dayKey = formatDateYMD(dayDate);
        daysList.push({ day, dayDate, dayKey });
      }
    }
    return daysList;
  }, [selectedDate, groupedEvents, hideEmptyDays]);

  // Generate PDF
  const generatePDF = () => {
    setIsGeneratingPDF(true);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const tableWidth = pageWidth - 2 * margin;
    let startY = 45;

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("ASpiceAffair", margin, 20);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    // doc.text("123 Flavor Street, Spiceville, SP 12345", margin, 30);
        doc.text("1560 55th Ave, Montréal, QC H8T 3J5, Canada", margin, 30);
    doc.text("Email: contact@aspiceaffair.com", margin, 35);

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Work Orders - ${roomFilter}`, pageWidth - margin, 20, {
      align: "right",
    });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    if (view === "day") {
      doc.text(
        selectedDate.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        margin,
        startY,
      );
    } else {
      const { weekStart, weekEnd } = getWeekRange(selectedDate);
      doc.text(
        `${weekStart.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ` +
          `${weekEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`,
        margin,
        startY,
      );
    }
    startY += 15;

    const columns =
      sortBy === "hourly"
        ? ["Time", "WO #", "Product Name", "Label", "Qty", "Status"]
        : ["WO #", "Product Name", "Label", "Qty", "Scheduled Time", "Status"];

    if (view === "day" && sortBy === "hourly") {
      const dayRows = [];

      // Sort hours numerically
      Object.keys(dayEvents)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((hour) => {
          dayEvents[hour].forEach((wo) => {
            dayRows.push([
              wo.slot || wo.scheduledTime || "Not set",
              wo.woNumber || "—",
              wo.title || "—",
              wo.label || "—",
              wo.plannedQty ?? "—",
              wo.status || "Unknown",
            ]);
          });
        });

      if (dayRows.length === 0) {
        doc.setFontSize(12);
        doc.text(
          `No work orders scheduled for ${roomFilter} on this date.`,
          margin,
          startY,
        );
        startY += 15;
      } else {
        autoTable(doc, {
          startY,
          head: [columns],
          body: dayRows,
          theme: "grid",
          styles: {
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
            lineHeight: 1.2,
          },
          headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
          margin: { left: margin, right: margin },
          columnStyles: {
            0: { cellWidth: tableWidth * 0.16 },
            1: { cellWidth: tableWidth * 0.14 },
            2: { cellWidth: tableWidth * 0.28 },
            3: { cellWidth: tableWidth * 0.14 },
            4: { cellWidth: tableWidth * 0.14 },
            5: { cellWidth: tableWidth * 0.14 },
          },
        });
        startY = doc.lastAutoTable.finalY + 15;
      }
    }

    else if (sortBy === "room") {
      Object.keys(groupedEvents).forEach((roomKey) => {
        const roomRows = groupedEvents[roomKey].map((wo) => [
          wo.woNumber || "—",
          wo.title || "—",
          wo.label || "—",
          wo.plannedQty ?? "—",
          `${wo.scheduledTime || "Not set"} (${wo.duration || "?"} min)`,
          wo.status || "Unknown",
        ]);

        if (roomRows.length === 0) return;

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(roomKey, margin, startY);
        startY += 10;

        autoTable(doc, {
          startY,
          head: [columns],
          body: roomRows,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
          headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
          margin: { left: margin, right: margin },
          columnStyles: {
            0: { cellWidth: tableWidth * 0.14 },
            1: { cellWidth: tableWidth * 0.28 },
            2: { cellWidth: tableWidth * 0.14 },
            3: { cellWidth: tableWidth * 0.14 },
            4: { cellWidth: tableWidth * 0.16 },
            5: { cellWidth: tableWidth * 0.14 },
          },
        });
        startY = doc.lastAutoTable.finalY + 15;
      });
    }
    else {
      const { weekStart } = getWeekRange(selectedDate);
      for (let day = 0; day < 7; day++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + day);
        const dayKey = formatDateYMD(dayDate);

        let hasWorkOrders = false;
        Object.keys(groupedEvents[dayKey] || {}).forEach((h) => {
          if (groupedEvents[dayKey][h]?.length > 0) hasWorkOrders = true;
        });

        if (hideEmptyDays && !hasWorkOrders) continue;

        if (hasWorkOrders) {
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text(
            dayDate.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            }),
            margin,
            startY,
          );
          startY += 10;
        }

        const dayRows = [];
        Object.keys(groupedEvents[dayKey] || {}).forEach((hour) => {
          groupedEvents[dayKey][hour].forEach((wo) => {
            dayRows.push([
              `${wo.scheduledTime || "Not set"} (${wo.duration || "?"} min)`,
              wo.woNumber || "—",
              wo.title || "—",
              wo.label || "—",
              wo.plannedQty ?? "—",
              wo.status || "Unknown",
            ]);
          });
        });

        if (dayRows.length > 0) {
          autoTable(doc, {
            startY,
            head: [columns],
            body: dayRows,
            theme: "grid",
            styles: { fontSize: 8.5, cellPadding: 2.5, overflow: "linebreak" },
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
            margin: { left: margin, right: margin },
            columnStyles: {
              0: { cellWidth: tableWidth * 0.18 },
              1: { cellWidth: tableWidth * 0.14 },
              2: { cellWidth: tableWidth * 0.28 },
              3: { cellWidth: tableWidth * 0.14 },
              4: { cellWidth: tableWidth * 0.12 },
              5: { cellWidth: tableWidth * 0.14 },
            },
          });
          startY = doc.lastAutoTable.finalY + 12;
        }
      }
    }
    if (startY <= 60) {
      // barely moved after title
      doc.setFontSize(12);
      doc.text(
        `No work orders found for ${roomFilter} in the selected view / period.`,
        margin,
        startY,
      );
      toast.info("No data to display in PDF – check filters or data.", {
        duration: 5000,
        position: "top-right",
      });
    } else {
      toast.success(`Work Orders PDF for ${roomFilter} generated!`, {
        duration: 4000,
        position: "top-right",
      });
    }

    // Footer disclaimer
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(
      "Please verify work order details before use.",
      margin,
      doc.internal.pageSize.getHeight() - 15,
    );

    doc.output("dataurlnewwindow", {
      filename: `work_orders_${roomFilter.replace(/\s+/g, "_")}_${formatDateYMD(selectedDate)}.pdf`,
    });

    setIsGeneratingPDF(false);
  };

  const { weekStart, weekEnd } = getWeekRange(selectedDate);

  return (
    <div className="p-4 sm:p-5 bg-gray-50 min-h-screen">
      {/* Controls Section */}
      <div className="mb-6 bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
        <div className="flex flex-col gap-5">
          {/* Navigation Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={goToPreviousWeek}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg shadow-sm"
                aria-label="Previous Week"
              >
                ◀
              </Button>

              <span className="text-sm sm:text-base font-semibold text-gray-800">
                {weekStart.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                –{" "}
                {weekEnd.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>

              <Button
                onClick={goToNextWeek}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg shadow-sm"
                aria-label="Next Week"
              >
                ▶
              </Button>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={goToCurrentWeek}
                className="px-4 py-2 bg-black hover:bg-gray-900 text-white rounded-lg shadow-sm"
                aria-label="Current Week"
              >
                This Week
              </Button>
            </div>
          </div>

          {/* Quick Room Shortcuts Strip */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider shrink-0 mr-1">
              Rooms:
            </span>
            <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
              {rooms.map((room) => {
                const isActive = roomFilter === room;
                return (
                  <button
                    key={room}
                    type="button"
                    onClick={() => setRoomFilter(room)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all shadow-sm ${
                      isActive
                        ? "bg-black text-white shadow font-semibold"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                    }`}
                  >
                    {room}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-3 shadow-sm gap-3">
            {/* Left side filters */}
            <div className="flex flex-wrap items-center gap-3">
              <SingleDatePicker
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                className="min-w-[180px] h-[42px]"
              />

              <Select
                value={view}
                onChange={(e) => setView(e.target.value)}
                options={[
                  { value: "day", label: "Day" },
                  { value: "week", label: "Week" },
                ]}
                className="h-[42px] min-w-[120px]"
              />

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { value: "hourly", label: "Hourly" },
                  { value: "room", label: "Room" },
                ]}
                className="h-[42px] min-w-[140px]"
              />

              <Select
                value={roomFilter}
                onChange={(e) => setRoomFilter(e.target.value)}
                options={roomOptions}
                className="h-[42px] min-w-[140px]"
              />
              {view === "week" && (
                <button
                  type="button"
                  onClick={() => setHideEmptyDays(!hideEmptyDays)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                    hideEmptyDays
                      ? "bg-[#d73094] text-white border-[#d73094]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {hideEmptyDays ? "Show Empty Days" : "Hide Empty Days"}
                </button>
              )}
            </div>

            {/* Right side print button */}
            <div>
              <Button
                onClick={generatePDF}
                className="bg-black hover:bg-gray-900 text-white flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm h-[42px]"
                disabled={isGeneratingPDF}
                aria-label="Generate PDF"
              >
                {isGeneratingPDF ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8h8a8 8 0 11-16 0z"
                    />
                  </svg>
                ) : (
                  <PrinterIcon className="w-5 h-5" />
                )}
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      {view === "day" && sortBy === "hourly" ? (
        <div className="border border-gray-200 rounded-lg shadow-md overflow-y-auto max-h-[70vh] sm:max-h-[80vh]">
          {hours.map((hour) => (
            <div
              key={hour}
              className="flex flex-col sm:flex-row border-b border-gray-200 hover:bg-gray-50 transition-colors"
              style={{ minHeight: "60px" }}
            >
              <div className="w-full sm:w-20 p-2 sm:p-3 text-xs sm:text-sm text-gray-600 bg-gray-100 border-b sm:border-b-0 sm:border-r border-gray-200 sticky left-0 sm:sticky top-0">
                {hour % 12 || 12}:00 {hour < 12 ? "AM" : "PM"}
              </div>
              <div className="flex-1 p-2 sm:p-3">
                {(dayEvents[hour] || []).length === 0 ? (
                  <div className="text-xs sm:text-sm text-gray-500">
                    No work orders
                  </div>
                ) : (
                  (dayEvents[hour] || []).map((wo) => (
                    <div
                      key={wo.woNumber}
                      className="bg-indigo-50 p-2 sm:p-3 mb-2 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="text-xs sm:text-sm grid grid-cols-2 gap-1 sm:flex sm:flex-wrap sm:gap-2 items-center">
                        <span className="font-semibold text-indigo-700">
                          {wo.slot ||
                            `${wo.scheduledTime} (${wo.duration} min)`}
                        </span>
                        <span className="text-gray-800">{wo.woNumber}</span>
                        <span className="text-gray-800">
                          {wo.title}{" "}
                          <span
                            className={`font-semibold ${
                              wo.status === "Completed"
                                ? "text-green-600"
                                : "text-yellow-600"
                            }`}
                          >
                            ({wo.status})
                          </span>
                        </span>
                        <span className="text-gray-600">{wo.label}</span>
                        <span className="text-gray-600">
                          Qty: {wo.plannedQty}
                        </span>
                        <span className="font-semibold text-gray-800">
                          Room: {wo.room}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg shadow-md overflow-auto max-h-[70vh] sm:max-h-[80vh]">
          {sortBy === "room" ? (
            Object.keys(groupedEvents).length === 0 ? (
              <div className="p-4 sm:p-6 text-center text-gray-500 text-xs sm:text-sm">
                No work orders for {roomFilter}. Try changing filters.
              </div>
            ) : (
              Object.keys(groupedEvents).map((room) => (
                <div key={room} className="border-b border-gray-200 mb-4">
                  <div className="bg-gray-100 p-2 sm:p-3 font-semibold text-gray-900 sticky top-0 z-10 text-xs sm:text-sm">
                    {room}
                  </div>
                  <div className="p-2 sm:p-3">
                    {groupedEvents[room].length === 0 ? (
                      <div className="text-xs sm:text-sm text-gray-500">
                        No work orders for this room
                      </div>
                    ) : (
                      <table className="w-full bg-indigo-50 rounded-lg shadow-sm">
                        <thead>
                          <tr className="bg-gray-200 text-gray-900 text-xs sm:text-sm">
                            <th className="p-2 sm:p-3 text-left">WO #</th>
                            <th className="p-2 sm:p-3 text-left">
                              Scheduled Time
                            </th>
                            <th className="p-2 sm:p-3 text-left">
                              Product Name
                            </th>
                            <th className="p-2 sm:p-3 text-left">Label</th>
                            <th className="p-2 sm:p-3 text-left">Qty</th>

                            <th className="p-2 sm:p-3 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedEvents[room].map((wo) => (
                            <tr
                              key={wo.woNumber}
                              className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-800">
                                {wo.woNumber}
                              </td>

                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">
                                {wo.slot ||
                                  `${wo.scheduledTime} (${wo.duration} min)`}
                              </td>
                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-800">
                                {wo.title}{" "}
                              </td>
                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">
                                {wo.label}
                              </td>
                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">
                                {wo.plannedQty}
                              </td>

                              <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">
                                {wo.status}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
            <div
              className="min-w-full"
              style={{
                minWidth: `${Math.max(visibleDays.length * 140, 750)}px`,
              }}
            >
              {/* Sticky Week Days Header */}
              <div
                className="grid gap-px bg-gray-200 sticky top-0 z-20 shadow-sm"
                style={{
                  gridTemplateColumns: `repeat(${visibleDays.length}, minmax(140px, 1fr))`,
                }}
              >
                {visibleDays.map(({ dayKey, dayDate }) => (
                  <div
                    key={dayKey}
                    className="bg-gray-100 p-2 sm:p-3 text-center font-semibold text-gray-900 text-xs sm:text-sm select-none"
                  >
                    {dayDate.toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                ))}
              </div>

              {/* Hour Rows */}
              <div className="flex flex-col gap-px bg-gray-200">
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="grid gap-px bg-gray-200 border-b border-gray-200"
                    style={{
                      gridTemplateColumns: `repeat(${visibleDays.length}, minmax(140px, 1fr))`,
                    }}
                  >
                    {visibleDays.map(({ dayKey }) => {
                      const events = groupedEvents[dayKey]?.[hour] || [];
                      return (
                        <div
                          key={dayKey}
                          className="bg-white p-2 min-h-[58px] hover:bg-gray-50/80 transition-colors flex flex-col justify-start border-l border-gray-200"
                        >
                          <div className="text-xs text-gray-500 mb-1 font-medium select-none">
                            {hour % 12 || 12}:00 {hour < 12 ? "AM" : "PM"}
                          </div>

                          <div className="space-y-1.5 flex-1">
                            {events.map((wo) => (
                              <div
                                key={wo.woNumber}
                                className="group relative bg-indigo-50/90 hover:bg-indigo-100/90 border border-indigo-200/80 rounded-lg p-2 text-xs cursor-default transition-all shadow-sm hover:shadow"
                              >
                                {/* Top Row: WO# */}
                                <div className="flex items-center justify-between gap-1 mb-0.5">
                                  <span className="font-bold text-[10px] text-indigo-700 bg-indigo-100/90 px-1.5 py-0.5 rounded border border-indigo-200 shrink-0">
                                    {wo.woNumber}
                                  </span>
                                </div>

                                {/* Second Row: Full Time Slot (No truncation) */}
                                {(wo.slot || wo.scheduledTime) && (
                                  <div
                                    className="font-semibold text-indigo-900 text-[10px] sm:text-[11px] mb-1 leading-tight tracking-tight"
                                    title={wo.slot || wo.scheduledTime}
                                  >
                                    {wo.slot || wo.scheduledTime}
                                  </div>
                                )}

                                {/* FG ITEM NAME EN */}
                                <div
                                  className="font-semibold text-gray-900 text-xs leading-snug line-clamp-2 mb-1.5 break-words"
                                  title={wo.title}
                                >
                                  {wo.title || "Untitled Item"}
                                </div>

                                {/* Footer: QTY & Status */}
                                <div className="flex md:flex-row 2xl:flex-row items-center justify-between gap-1 text-[11px] pt-1 border-t border-indigo-100">
                                  <span className="font-bold text-indigo-950 bg-white/90 px-1.5 py-0.5 rounded border border-indigo-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                                    Qty: {wo.plannedQty != null ? Number(wo.plannedQty).toLocaleString() : "-"}
                                  </span>
                                  {wo.status && (
                                    <span
                                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                        wo.status === "Completed"
                                          ? "bg-green-100 text-green-700"
                                          : wo.status === "Approved"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-amber-100 text-amber-700"
                                      }`}
                                    >
                                      {wo.status}
                                    </span>
                                  )}
                                </div>

                                {/* Hover Tooltip for extra details */}
                                <div
                                  className={`absolute left-0 px-2.5 py-1.5 bg-gray-800 text-white text-xs leading-tight rounded-md whitespace-normal opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50 shadow-lg w-60 max-w-[calc(100vw-3rem)] break-words ${
                                    hour <= 9
                                      ? "top-full mt-1.5"
                                      : "bottom-full mb-1.5"
                                  }`}
                                >
                                  <div className="font-semibold text-indigo-300">
                                    {wo.title}
                                  </div>
                                  <div className="text-gray-300 text-xs mt-0.5 space-x-2">
                                    <span className="inline-block">
                                      Qty: {wo.plannedQty}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span className="inline-block">
                                      {wo.label || "N/A"}
                                    </span>
                                  </div>
                                  <div className="text-gray-400 text-xs mt-0.5">
                                    {wo.room} • {wo.status}
                                  </div>
                                  <div
                                    className={`absolute left-6 w-0 h-0 border-4 border-transparent ${
                                      hour <= 9
                                        ? "bottom-full -mb-px border-b-gray-800"
                                        : "top-full -mt-px border-t-gray-800"
                                    }`}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrderCalendar;
